import React, { useCallback, useEffect, useState } from 'react';
import { Card, DatePicker, Select, Statistic, Row, Col, List, Pagination, Typography, Tag } from 'antd';
import {
  UserOutlined,
  FileTextOutlined,
  EditOutlined,
  FolderOutlined,
} from '@ant-design/icons';
import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import dayjs from 'dayjs';
import {
  getDashboard,
  getVisitorPages,
  getVisitorSummary,
  getVisitors,
  getVisitorsChart,
} from '@/services/api';
import { useTheme } from '@/contexts/ThemeContext';

const { RangePicker } = DatePicker;
const CHART_PRESET_DAYS = {
  week: 7,
  month: 30,
  '3month': 90,
  '6month': 180,
  year: 365,
};

const Dashboard = () => {
  const VISITOR_LIST_LIMIT = 20;
  const { isDark } = useTheme();
  const [stats, setStats] = useState({
    totalUsers: 0,
    totalArticles: 0,
    totalWriters: 0,
    totalCategories: 0,
  });
  const [recentTopArticles, setRecentTopArticles] = useState([]);
  const [recentVisitors, setRecentVisitors] = useState([]);
  const [visitorTrend, setVisitorTrend] = useState([]);
  const [chartPreset, setChartPreset] = useState('month');
  const [chartDateRange, setChartDateRange] = useState(() => [
    dayjs().subtract(29, 'day'),
    dayjs(),
  ]);
  const [visitorPage, setVisitorPage] = useState(1);
  const [visitorPagination, setVisitorPagination] = useState({
    page: 1,
    limit: VISITOR_LIST_LIMIT,
    total: 0,
    totalPages: 0,
  });
  const [visitorAnalytics, setVisitorAnalytics] = useState({
    totalRecords: 0,
    uniqueVisitors: 0,
    newVisits: 0,
    repeatedVisits: 0,
    topCountries: [],
    topBrowsers: [],
    topDevices: [],
  });
  const [pageStats, setPageStats] = useState([]);
  const [pageStatsPage, setPageStatsPage] = useState(1);
  const [pageStatsPagination, setPageStatsPagination] = useState({
    page: 1,
    limit: 50,
    total: 0,
    totalPages: 0,
  });

  const normalizeChartData = useCallback((chartPayload) => {
    if (!chartPayload) return [];

    const labels = chartPayload?.labels || [];
    const datasets = chartPayload?.datasets || [];

    const uniqueDataset = datasets.find((dataset) =>
      String(dataset?.label || '').toLowerCase().includes('unique'),
    );
    const totalDataset = datasets.find((dataset) =>
      String(dataset?.label || '').toLowerCase().includes('total'),
    );
    const repeatedDataset = datasets.find((dataset) =>
      String(dataset?.label || '').toLowerCase().includes('repeated'),
    );

    return labels.map((date, index) => ({
      date,
      totalVisitors: Number(totalDataset?.data?.[index] ?? 0),
      uniqueVisitors: Number(uniqueDataset?.data?.[index] ?? 0),
      repeatedVisitors: Number(repeatedDataset?.data?.[index] ?? 0),
    }));
  }, []);

  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        const dashboardRes = await getDashboard();
        if (!dashboardRes?.isError && dashboardRes?.data?.statuscode === 1) {
          const totals = dashboardRes?.data?.totals || {};
          setStats({
            totalUsers: totals?.users || 0,
            totalArticles: totals?.articles || 0,
            totalWriters: totals?.writers || 0,
            totalCategories: totals?.categories || 0,
          });
          setRecentTopArticles(dashboardRes?.data?.recentTopArticles || []);
        }
      } catch (error) {
        console.error('Failed to fetch dashboard stats:', error);
      }
    };

    const fetchVisitorSummary = async () => {
      try {
        const summaryRes = await getVisitorSummary();
        if (summaryRes?.isError || summaryRes?.data?.statuscode !== 1) {
          return;
        }

        setVisitorAnalytics({
          totalRecords: summaryRes?.data?.data?.totalRecords || 0,
          uniqueVisitors: summaryRes?.data?.data?.uniqueVisitors || 0,
          newVisits: summaryRes?.data?.data?.newVisits || 0,
          repeatedVisits: summaryRes?.data?.data?.repeatedVisits || 0,
          topCountries: summaryRes?.data?.data?.topCountries || [],
          topBrowsers: summaryRes?.data?.data?.topBrowsers || [],
          topDevices: summaryRes?.data?.data?.topDevices || [],
        });
      } catch (error) {
        console.error('Failed to fetch visitor analytics:', error);
      }
    };

    fetchDashboardData();
    fetchVisitorSummary();
  }, []);

  useEffect(() => {
    const fetchVisitorChart = async () => {
      try {
        const [startDateObj, endDateObj] = chartDateRange || [];
        if (!startDateObj || !endDateObj) {
          setVisitorTrend([]);
          return;
        }

        const chartRes = await getVisitorsChart({
          startDate: startDateObj.format('YYYY-MM-DD'),
          endDate: endDateObj.format('YYYY-MM-DD'),
        });

        if (chartRes?.isError || chartRes?.data?.statuscode !== 1) {
          setVisitorTrend([]);
          return;
        }

        setVisitorTrend(normalizeChartData(chartRes?.data?.chart));
      } catch (error) {
        console.error('Failed to fetch visitors chart:', error);
      }
    };

    fetchVisitorChart();
  }, [chartDateRange, normalizeChartData]);

  useEffect(() => {
    const fetchVisitorPage = async () => {
      try {
        const visitorsRes = await getVisitors({
          page: visitorPage,
          limit: VISITOR_LIST_LIMIT,
        });

        if (visitorsRes?.isError || visitorsRes?.data?.statuscode !== 1) {
          return;
        }

        setRecentVisitors(visitorsRes?.data?.data || []);
        setVisitorPagination(
          visitorsRes?.data?.pagination || {
            page: visitorPage,
            limit: VISITOR_LIST_LIMIT,
            total: 0,
            totalPages: 0,
          },
        );
      } catch (error) {
        console.error('Failed to fetch visitors list:', error);
      }
    };

    fetchVisitorPage();
  }, [visitorPage]);

  useEffect(() => {
    const fetchVisitorPages = async () => {
      try {
        const pageRes = await getVisitorPages({ page: pageStatsPage, limit: 50 });
        if (pageRes?.isError || pageRes?.data?.statuscode !== 1) {
          return;
        }

        setPageStats(pageRes?.data?.data || []);
        setPageStatsPagination(
          pageRes?.data?.pagination || {
            page: pageStatsPage,
            limit: 50,
            total: 0,
            totalPages: 0,
          },
        );
      } catch (error) {
        console.error('Failed to fetch visitor page stats:', error);
      }
    };

    fetchVisitorPages();
  }, [pageStatsPage]);

  const cardStyle = {
    borderRadius: 0,
    border: `1px solid ${isDark ? '#27272A' : '#E4E4E7'}`,
    background: isDark ? '#18181B' : '#FFFFFF',
    transition: 'border-color 0.2s',
  };

  const statCards = [
    { title: 'Total Users', value: stats.totalUsers, icon: <UserOutlined style={{ fontSize: 32, color: '#18181B' }} />, color: '#10B981' },
    { title: 'Total Articles', value: stats.totalArticles, icon: <FileTextOutlined style={{ fontSize: 32, color: '#18181B' }} />, color: '#3B82F6' },
    { title: 'Total Writers', value: stats.totalWriters, icon: <EditOutlined style={{ fontSize: 32, color: '#18181B' }} />, color: '#F59E0B' },
    { title: 'Total Categories', value: stats.totalCategories, icon: <FolderOutlined style={{ fontSize: 32, color: '#18181B' }} />, color: '#DC2626' },
  ];

  const handleChartDateChange = (dates) => {
    if (!dates || !dates[0] || !dates[1]) {
      setChartPreset('month');
      setChartDateRange([dayjs().subtract(29, 'day'), dayjs()]);
      return;
    }
    setChartPreset(null);
    setChartDateRange(dates);
  };

  const handleChartPresetChange = (value) => {
    const days = CHART_PRESET_DAYS[value] || 30;
    const end = dayjs();
    const start = end.subtract(days - 1, 'day');
    setChartPreset(value);
    setChartDateRange([start, end]);
  };

  return (
    <div>
      <div style={{ marginBottom: 32 }}>
        <h1 style={{ fontSize: 28, fontWeight: 700, fontFamily: 'IBM Plex Sans, sans-serif', letterSpacing: '-0.02em', marginBottom: 8 }}>
          Dashboard
        </h1>
        <p style={{ fontSize: 14, color: isDark ? '#A1A1AA' : '#71717A', margin: 0 }}>
          Overview of your news platform
        </p>
      </div>

      <Row gutter={[24, 24]}>
        {statCards.map((stat, index) => (
          <Col xs={24} sm={12} lg={6} key={index}>
            <Card
              style={cardStyle}
              onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = isDark ? '#FAFAFA' : '#18181B';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = isDark ? '#27272A' : '#E4E4E7';
              }}
              data-testid={`stat-card-${stat.title.toLowerCase().replace(/ /g, '-')}`}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Statistic
                  title={
                    <span style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.05em', color: isDark ? '#A1A1AA' : '#71717A', fontWeight: 600 }}>
                      {stat.title}
                    </span>
                  }
                  value={stat.value}
                  valueStyle={{
                    fontSize: 32,
                    fontWeight: 700,
                    fontFamily: 'JetBrains Mono, monospace',
                    color: isDark ? '#FAFAFA' : '#18181B',
                  }}
                />
                <div style={{ opacity: 0.6 }}>
                  {stat.icon}
                </div>
              </div>
            </Card>
          </Col>
        ))}
      </Row>

      <Row gutter={[24, 24]} style={{ marginTop: 24 }}>
        <Col xs={24} lg={16}>
          <Card
            title={
              <span style={{ fontSize: 16, fontWeight: 600, fontFamily: 'IBM Plex Sans, sans-serif' }}>
                Recent Activity
              </span>
            }
            style={cardStyle}
          >
            <div style={{ padding: '16px 0' }}>
              <List
                dataSource={recentTopArticles}
                locale={{ emptyText: 'No recent top articles.' }}
                renderItem={(article) => (
                  <List.Item key={article.uid} style={{ borderBlockEndColor: isDark ? '#27272A' : '#F0F0F0' }}>
                    <div style={{ width: '100%' }}>
                      <Typography.Text style={{ fontWeight: 600, color: isDark ? '#FAFAFA' : '#18181B' }}>
                        {article.title}
                      </Typography.Text>
                      <div style={{ marginTop: 8, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                        <Tag style={{ borderRadius: 0 }}>{article.category || 'Uncategorized'}</Tag>
                        <Tag style={{ borderRadius: 0 }}>{article.author?.name || 'Unknown Author'}</Tag>
                        <Tag style={{ borderRadius: 0 }}>
                          {article.date ? new Date(article.date).toLocaleString() : '-'}
                        </Tag>
                      </div>
                    </div>
                  </List.Item>
                )}
              />
            </div>
          </Card>
        </Col>
        <Col xs={24} lg={8}>
          <Card
            title={
              <span style={{ fontSize: 16, fontWeight: 600, fontFamily: 'IBM Plex Sans, sans-serif' }}>
                Visitor Analytics
              </span>
            }
            style={cardStyle}
          >
            <div style={{ padding: '16px 0' }}>
              <Row gutter={[12, 12]}>
                <Col span={12}>
                  <Statistic title="Total Visits" value={visitorAnalytics.totalRecords} />
                </Col>
                <Col span={12}>
                  <Statistic title="Unique IPs" value={visitorAnalytics.uniqueVisitors} />
                </Col>
                <Col span={12}>
                  <Statistic title="New Visits" value={visitorAnalytics.newVisits} />
                </Col>
                <Col span={12}>
                  <Statistic title="Repeated" value={visitorAnalytics.repeatedVisits} />
                </Col>
              </Row>
              <div style={{ marginTop: 16 }}>
                <Typography.Text strong style={{ display: 'block', marginBottom: 8 }}>
                  Top Countries
                </Typography.Text>
                {visitorAnalytics.topCountries.map((item) => (
                  <Tag key={`country-${item.label}`} style={{ borderRadius: 0, marginBottom: 6 }}>
                    {item.label} ({item.value})
                  </Tag>
                ))}
              </div>
              <div style={{ marginTop: 12 }}>
                <Typography.Text strong style={{ display: 'block', marginBottom: 8 }}>
                  Browsers
                </Typography.Text>
                {visitorAnalytics.topBrowsers.map((item) => (
                  <Tag key={`browser-${item.label}`} style={{ borderRadius: 0, marginBottom: 6 }}>
                    {item.label} ({item.value})
                  </Tag>
                ))}
              </div>
              <div style={{ marginTop: 12 }}>
                <Typography.Text strong style={{ display: 'block', marginBottom: 8 }}>
                  Devices
                </Typography.Text>
                {visitorAnalytics.topDevices.map((item) => (
                  <Tag key={`device-${item.label}`} style={{ borderRadius: 0, marginBottom: 6 }}>
                    {item.label} ({item.value})
                  </Tag>
                ))}
              </div>
            </div>
          </Card>
        </Col>
      </Row>

      <Row gutter={[24, 24]} style={{ marginTop: 24 }}>
        <Col xs={24}>
          <Card
            title={
              <span style={{ fontSize: 16, fontWeight: 600, fontFamily: 'IBM Plex Sans, sans-serif' }}>
                Visitor Trend (Unique vs Repeated)
              </span>
            }
            style={cardStyle}
          >
            <div style={{ marginBottom: 16, display: 'flex', gap: 12, flexWrap: 'wrap' }}>
              <Select
                value={chartPreset || undefined}
                onChange={handleChartPresetChange}
                style={{ minWidth: 170 }}
                data-testid="visitor-chart-range-preset"
                options={[
                  { value: 'week', label: 'Last Week' },
                  { value: 'month', label: 'Last 30 Days' },
                  { value: '3month', label: 'Last 3 Months' },
                  { value: '6month', label: 'Last 6 Months' },
                  { value: 'year', label: 'Last 1 Year' },
                ]}
              />
              <RangePicker
                value={chartDateRange}
                onChange={handleChartDateChange}
                format="YYYY-MM-DD"
                allowClear
                data-testid="visitor-chart-date-filter"
              />
            </div>
            <div style={{ width: '100%', height: 320 }}>
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={visitorTrend}>
                  <CartesianGrid strokeDasharray="3 3" stroke={isDark ? '#27272A' : '#E5E7EB'} />
                  <XAxis dataKey="date" stroke={isDark ? '#A1A1AA' : '#71717A'} />
                  <YAxis allowDecimals={false} stroke={isDark ? '#A1A1AA' : '#71717A'} />
                  <Tooltip />
                  <Legend />
                  <Line
                    type="monotone"
                    dataKey="totalVisitors"
                    name="Total Visitors"
                    stroke="#22C55E"
                    strokeWidth={2}
                    dot={{ r: 3 }}
                  />
                  <Line
                    type="monotone"
                    dataKey="uniqueVisitors"
                    name="Unique Visitors"
                    stroke="#3B82F6"
                    strokeWidth={2}
                    dot={{ r: 3 }}
                  />
                  <Line
                    type="monotone"
                    dataKey="repeatedVisitors"
                    name="Repeated Visitors"
                    stroke="#F97316"
                    strokeWidth={2}
                    dot={{ r: 3 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </Card>
        </Col>
      </Row>

      <Row gutter={[24, 24]} style={{ marginTop: 24 }}>
        <Col xs={24}>
          <Card
            title={
              <span style={{ fontSize: 16, fontWeight: 600, fontFamily: 'IBM Plex Sans, sans-serif' }}>
                Top Visited Pages
              </span>
            }
            style={cardStyle}
          >
            <List
              dataSource={pageStats}
              locale={{ emptyText: 'No page stats found.' }}
              renderItem={(page) => (
                <List.Item style={{ borderBlockEndColor: isDark ? '#27272A' : '#F0F0F0' }}>
                  <div style={{ width: '100%' }}>
                    <Typography.Text style={{ fontWeight: 600, color: isDark ? '#FAFAFA' : '#18181B' }}>
                      {page.pageTitle || page.pagePath || page.pageUrl}
                    </Typography.Text>
                    <div style={{ marginTop: 8, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                      <Tag style={{ borderRadius: 0 }}>{page.pagePath || 'Unknown Path'}</Tag>
                      <Tag style={{ borderRadius: 0 }}>Visits: {page.visitCount ?? 0}</Tag>
                      <Tag style={{ borderRadius: 0 }}>
                        Last: {page.lastVisit ? new Date(page.lastVisit).toLocaleString() : '-'}
                      </Tag>
                    </div>
                  </div>
                </List.Item>
              )}
            />
            <div style={{ marginTop: 16, display: 'flex', justifyContent: 'flex-end' }}>
              <Pagination
                current={pageStatsPagination.page || pageStatsPage}
                pageSize={pageStatsPagination.limit || 50}
                total={pageStatsPagination.total || 0}
                onChange={(page) => setPageStatsPage(page)}
                showSizeChanger={false}
              />
            </div>
          </Card>
        </Col>
      </Row>

      <Row gutter={[24, 24]} style={{ marginTop: 24 }}>
        <Col xs={24}>
          <Card
            title={
              <span style={{ fontSize: 16, fontWeight: 600, fontFamily: 'IBM Plex Sans, sans-serif' }}>
                Recent Visitors
              </span>
            }
            style={cardStyle}
          >
            <List
              dataSource={recentVisitors}
              locale={{ emptyText: 'No visitors found.' }}
              renderItem={(visitor) => (
                <List.Item style={{ borderBlockEndColor: isDark ? '#27272A' : '#F0F0F0' }}>
                  <div style={{ width: '100%' }}>
                    <Typography.Text style={{ fontWeight: 600, color: isDark ? '#FAFAFA' : '#18181B' }}>
                      {visitor.ipAddress} - {visitor.city}, {visitor.country}
                    </Typography.Text>
                    <div style={{ marginTop: 8, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                      <Tag style={{ borderRadius: 0 }}>{visitor.browser || 'Unknown Browser'}</Tag>
                      <Tag style={{ borderRadius: 0 }}>{visitor.os || 'Unknown OS'}</Tag>
                      <Tag style={{ borderRadius: 0 }}>{visitor.deviceType || 'Unknown Device'}</Tag>
                      <Tag style={{ borderRadius: 0 }}>
                        {Number(visitor.visitCount || 0) <= 1 ? 'New Visit' : 'Repeated Visit'}
                      </Tag>
                      <Tag style={{ borderRadius: 0 }}>
                        Last: {visitor.lastVisit ? new Date(visitor.lastVisit).toLocaleString() : '-'}
                      </Tag>
                    </div>
                  </div>
                </List.Item>
              )}
            />
            <div style={{ marginTop: 16, display: 'flex', justifyContent: 'flex-end' }}>
              <Pagination
                current={visitorPagination.page || visitorPage}
                pageSize={visitorPagination.limit || VISITOR_LIST_LIMIT}
                total={visitorPagination.total || 0}
                onChange={(page) => setVisitorPage(page)}
                showSizeChanger={false}
              />
            </div>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default Dashboard;
